import { supabase } from "@/lib/supabase/client";

export interface CommitmentRow {
  id: string;
  tenant_id: string;
  fund_id: string;
  lp_id: string;
  commitment_usd: number;
  closing_date: string | null;
  closing_round: string | null;
  status: "active" | "transferred" | "withdrawn";
}

const COLS = "id, tenant_id, fund_id, lp_id, commitment_usd, closing_date, closing_round, status";

export async function listCommitmentsByFund(
  tenantId: string,
  fundId: string,
): Promise<CommitmentRow[]> {
  const { data, error } = await supabase
    .from("fund_lp_commitments")
    .select(COLS)
    .eq("tenant_id", tenantId)
    .eq("fund_id", fundId)
    .eq("status", "active");
  if (error) throw new Error(`listCommitmentsByFund: ${error.message}`);
  return data ?? [];
}

export interface CreateCommitmentInput {
  tenant_id: string;
  fund_id: string;
  lp_id: string;
  commitment_usd: number;
  closing_date?: string | null;
  closing_round?: string | null;
}

export async function createCommitment(input: CreateCommitmentInput): Promise<void> {
  const { error } = await supabase.from("fund_lp_commitments").insert(input);
  if (error) throw new Error(`createCommitment: ${error.message}`);

  // Continuous MFN enforcement (build doc — 2026-07-01 decision): a new or
  // changed commitment can change which LPs are large enough to hold, or be
  // size-tested against, an MFN clause — re-evaluate the fund. There is no
  // commitments UI yet (grepped apps/web/src/pages: no page calls this
  // function today), so this is the seam: wired here at the source rather
  // than at a UI call site, so continuous enforcement fires automatically the
  // moment any caller — present or future — creates a commitment. A dynamic
  // import avoids a static api/ → pipeline/ → api/ cycle (continuous-mfn.ts
  // pulls in run-mfn.ts, which imports this module for
  // listCommitmentsByFund/commitmentMap). Fire-and-forget: never block or
  // fail the commitment write on enforcement. 2026-07-03 audit fix:
  // enforceMfnForFund can now throw on a genuine core-step failure (it no
  // longer silently swallows one to `null`) — log it here rather than
  // swallowing to `undefined` a second time, so a real failure still leaves a
  // trace even though this write must not be blocked by it.
  void import("@/lib/pipeline/continuous-mfn")
    .then(({ enforceMfnForFund }) =>
      enforceMfnForFund({
        tenantId: input.tenant_id,
        fundId: input.fund_id,
        source: "commitment_changed",
      }),
    )
    .catch((err: unknown) => {
      console.error(
        `[createCommitment] continuous MFN enforcement failed for fund=${input.fund_id}:`,
        err,
      );
    });
}

/** lpId → summed active commitment, the shape the MFN engine wants. */
export function commitmentMap(rows: CommitmentRow[]): Record<string, number> {
  const map: Record<string, number> = {};
  for (const r of rows) {
    map[r.lp_id] = (map[r.lp_id] ?? 0) + Number(r.commitment_usd);
  }
  return map;
}
